import React, { useState, useRef, useEffect, useContext, useCallback } from 'react';
import {
  Search,
  PanelLeft,
  Bell,
  User,
  Settings,
  HelpCircle,
  LogOut,
  ChevronDown,
  CheckCheck,
  FileText,
  Users,
  Tag,
  Mail,
  Lock,
  UserPlus,
  Download
} from 'lucide-react';
import './AdminHeader.css';
import { Link, useNavigate } from 'react-router-dom';
import { ApiUrl, BaseUrl } from '../API/ApiUrl';
import { ProfileContext } from '../Context/ProfileContext';
import ThemeToggle from './../utiles/ThemeToggle'

const AdminHeader = ({ onMenuClick }) => {
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [profile, setProfile] = useState(null);
  const { profileRefresh } = useContext(ProfileContext);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const notificationRef = useRef(null);
  const profileRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProfile();
  }, []);

  // Refetch profile when profileRefresh changes
  useEffect(() => {
    fetchProfile();
  }, [profileRefresh]);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem("adminToken");

      const response = await fetch(`${ApiUrl}/admin-profile`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      // console.log("Fetched profile:", data);

      setProfile(data);

      // set profile image
      if (data.profile_image) {
        setProfileImage(`${BaseUrl}/uploads/${data.profile_image}`);
      } else {
        setProfileImage(null);
      }

    } catch (error) {
      console.error("Profile fetch error:", error);
    }
  };


  const notificationIcons = {
    submission: { icon: FileText, color: "#2563eb" },
    reviewer: { icon: UserPlus, color: "#9333ea" },
    contact: { icon: Mail, color: "#16a34a" },
    download: { icon: Download, color: "#ea580c" }
  };


  const fetchNotifications = useCallback(async () => {
    try {

      const token = localStorage.getItem("adminToken");

      const response = await fetch(`${ApiUrl}/notifications?type=unread`, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });

      const data = await response.json();

      if (!response.ok) return;

      setNotifications(data);

      // calculate unread
      const unread = data.filter(n => n.is_read === 0).length;
      setUnreadCount(unread);

    } catch (error) {
      console.error("Notification fetch error:", error);
    }
  }, [])

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // const unreadCount = notifications.filter(n => !n.read).length;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfile(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  
  const markAllAsRead = async () => {
    try {

      const token = localStorage.getItem("adminToken");

      await fetch(`${ApiUrl}/notifications/read-all`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });

      fetchNotifications();
      window.dispatchEvent(new Event("notificationsUpdated"));

    } catch (error) {
      console.error(error);
    }

  };

  const markAsRead = async (id) => {

    try {

      const token = localStorage.getItem("adminToken");

      await fetch(`${ApiUrl}/notifications/${id}/read`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });

      fetchNotifications();
      window.dispatchEvent(new Event("notificationsUpdated"));
      
      // setNotifications(prev =>
      //   prev.map(n =>
      //     n.id === id ? { ...n, is_read: 1 } : n
      //   )
      // );

    } catch (error) {
      console.error(error);
    }

  };


  useEffect(() => {

    fetchNotifications();

    const handleUpdate = () => {
      fetchNotifications();
    };

    window.addEventListener("notificationsUpdated", handleUpdate);

    return () => {
      window.removeEventListener("notificationsUpdated", handleUpdate);
    };

  }, []);


  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const formatDate = (date) => {

    const now = new Date();
    const created = new Date(date);

    const diffMs = now - created;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) {
      return { date: "", time: "Just now" };
    }

    if (diffMinutes < 60) {
      return { date: "", time: `${diffMinutes} min${diffMinutes > 1 ? "s" : ""} ago` };
    }

    if (diffHours < 24) {
      return { date: "", time: `${diffHours} hour${diffHours > 1 ? "s" : ""} ago` };
    }

    return {
      date: created.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      }),
      time: created.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    };

  };


  return (
    <header className="admin-header">
      <div className="header-left">
        <button
          className="menu-toggle"
          onClick={onMenuClick}
          aria-label="Toggle menu"
        >
          <PanelLeft size={18} />
        </button>

        {/* <div className={`search-container ${searchFocused ? 'focused' : ''}`}>
          <Search size={16} className="search-icon" />
          <input
            type="text"
            placeholder="Search..."
            className="search-input"
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
          />
          <span className="search-hint">⌘K</span>
        </div> */}
      </div>

      <div className="header-right">

        <ThemeToggle />
        {/* Notifications */}
        <div className="notifications-wrapper" ref={notificationRef}>
          <button
            className={`action-btn notification-btn ${showNotifications ? 'active' : ''}`}
            onClick={() => setShowNotifications(!showNotifications)}
          >
            <Bell size={18} />
            {unreadCount > 0 && (
              <span className="notification-badge">{unreadCount}</span>
            )}
          </button>

          {showNotifications && (
            <div className="notifications-dropdown">
              <div className="dropdown-header">
                <div className="header-title">
                  <h3>Notifications</h3>
                  {unreadCount > 0 && (
                    <span className="header-badge">{unreadCount} new</span>
                  )}
                </div>
                <button
                  className="mark-read-btn"
                  onClick={markAllAsRead}
                  disabled={unreadCount === 0}
                >
                  <CheckCheck size={14} />
                  <span>Mark all read</span>
                </button>
              </div>
              {notifications.length === 0 && (
                <div className="empty-state">
                  <Bell size={48} />
                  <h3>No notifications found</h3>
                  <p>You're all caught up! Check back later for new notifications.</p>
                </div>
              )}

              <div className="notifications-list">
                {notifications.map((notification) => {

                  const iconData = notificationIcons[notification.type] || {};
                  const Icon = iconData.icon || Tag;
                  const color = iconData.color || "#64748b";
                  const { date, time } = formatDate(notification.created_at);

                  return (
                    <div
                      key={notification.id}
                      onClick={() => !notification.is_read && markAsRead(notification.id)}
                      className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                    >

                      <div
                        className="type-icon"
                        style={{ background: `${color}15` }}
                      >
                        <Icon size={18} style={{ color }} />
                      </div>

                      <div className="item-content">
                        <p className="item-text">{notification.title}</p>

                        <span className="item-message">
                          {notification.message}
                        </span>

                        <span className="item-time">
                          {date} {time}
                        </span>
                      </div>

                      {!notification.is_read && (
                        <span className="item-dot"></span>
                      )}

                    </div>
                  );
                })}
              </div>

              <div className="dropdown-footer">
                <Link to={"/admin/notifications"} className="footer-link" onClick={() => setShowNotifications(false)}>
                  View all
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Profile */}
        <div className="profile-wrapper" ref={profileRef}>
          <button
            className={`profile-btn ${showProfile ? 'active' : ''}`}
            onClick={() => setShowProfile(!showProfile)}
          >
            <div className="profile-avatar">
              {profileImage ? (
                <img
                  loading='lazy'

                  src={profileImage}
                  alt="Profile"
                  className="profile-image-icon"
                />
              ) : (
                <>
                  <User size={16} />
                </>
              )}
            </div>
            <div className="profile-info">
              <span className="profile-name">{profile?.name || "Admin"}</span>
              <span className="profile-role">{profile?.position || "Chief Editor"}</span>
            </div>
            <ChevronDown size={16} className={`profile-arrow ${showProfile ? 'rotated' : ''}`} />
          </button>

          {showProfile && (
            <div className="profile-dropdown">
              <div className="dropdown-user">
                <div className="user-avatar">
                  {profileImage ? (
                    <img
                      loading='lazy'

                      src={profileImage}
                      alt="Profile"
                      className="user-image-icon"
                    />
                  ) : (
                    <>
                      <User size={16} />
                    </>
                  )}
                </div>
                <div className="user-details">
                  <span className="user-name">{profile?.name || "Admin"}</span>
                  <span className="user-email">{profile?.email || "ijacna@gmail.com"}</span>
                </div>
              </div>

              <div className="dropdown-divider"></div>

              <Link to="/admin/profile" className="dropdown-item" onClick={() => setShowProfile(null)}>
                <User size={16} />
                <span>Profile</span>
              </Link>
              {/* <a href="/admin/profile" className="dropdown-item">
                <User size={16} />
                <span>Profile</span>
              </a> */}
              <Link to="/admin/change-password" className="dropdown-item" onClick={() => setShowProfile(null)}>
                <Lock size={16} />
                <span>Change Password</span>
              </Link>
              {/* <a href="#" className="dropdown-item">
                <HelpCircle size={16} />
                <span>Help</span>
              </a> */}

              <div className="dropdown-divider"></div>

              <p className="dropdown-item logout" onClick={handleLogout}>
                <LogOut size={16} />
                <span>Sign Out</span>
              </p>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default AdminHeader;